import * as XLSX from 'xlsx';
import { parseNumericValue, formatNumberForDisplay } from './numberUtils';

/**
 * Excel Export Utility for Capital Expenditure Data
 * Creates Excel files with the exact same format as the "नयाँ डाटा थप्नुहोस्" form
 */

// Excel styling constants
const EXCEL_STYLES = {
  // Main header - dark yellow/orange background with white text
  header: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "FFC000" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  },
  // Sub header - dark yellow/orange background with white text
  subHeader: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "FFC000" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  },
  // Category headers - light green background with black text
  categoryHeader: {
    font: { bold: true, color: { rgb: "000000" } },
    fill: { fgColor: { rgb: "D9EAD3" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  },
  // Data cells - white background with black borders
  dataCell: {
    font: { color: { rgb: "000000" } },
    fill: { fgColor: { rgb: "FFFFFF" } },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    },
    alignment: { horizontal: "center", vertical: "center" }
  },
  // Total rows - light green background with black bold text
  totalRow: {
    font: { bold: true, color: { rgb: "000000" } },
    fill: { fgColor: { rgb: "D9EAD3" } },
    alignment: { horizontal: "left", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  },
  // Add row buttons - vibrant green background with white text
  addRowButton: {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "6AA84F" } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  },
  // Number format for numerical cells
  numberFormat: {
    numFmt: "#,##0.00"
  }
};

/**
 * Generate Excel data with exact form format (30 columns) - Organized by expenditure records
 */
export const generateExcelData = (expenditureData) => {
  const { institution, fiscal_year, expenditure_items } = expenditureData;
  
  // Separate capital and operational items
  const capitalItems = expenditure_items?.filter(item => item.item_type === 'capital') || [];
  const operationalItems = expenditure_items?.filter(item => item.item_type === 'operational') || [];
  
  // Define the complete table structure with exactly 30 columns
  const excelData = [];
  
  // Header rows
  excelData.push(['मध्यपश्चिम विश्वबिधालय']);
  excelData.push([institution?.name || 'केन्द्रीय कार्यालय']);
  excelData.push([`आ .व ${fiscal_year?.display || ''} बजेट माग तथा खर्च विवरण`]);
  excelData.push([]); // Empty row
  
  // Main header row (30 columns exactly as in the form)
  const mainHeader = [
    'कोड नं.',
    'खर्च शिर्षक',
    'बिनियोजित बजेट',
    'परिमार्जीत बजेट',
    'प्रथम त्रैमासिक',
    '',
    '',
    '',
    '',
    'दोस्रो त्रैमासिक',
    '',
    '',
    '',
    '',
    'तेस्रो त्रैमासिक',
    '',
    '',
    '',
    '',
    'चौथो त्रैमासिक',
    '',
    '',
    '',
    '',
    'यस आ.व.को कुल बजेट विवरण',
    '',
    '',
    '',
    '',
    'विनियोजित बजेट र अर्च विचको भिन्नता'
  ];
  excelData.push(mainHeader);
  
  // Sub header row (30 columns exactly as in the form)
  const subHeader = [
    '',
    '',
    '',
    '',
    'बजेट माग',
    'बजेट निकासा',
    'अख्तियारी प्राप्त',
    'खर्च',
    'बाँकि मौज्दात',
    'बजेट माग',
    'बजेट निकासा',
    'अख्तियारी प्राप्त',
    'खर्च',
    'बाँकि मौज्दात',
    'बजेट माग',
    'बजेट निकासा',
    'अख्तियारी प्राप्त',
    'खर्च',
    'बाँकि मौज्दात',
    'बजेट माग',
    'बजेट निकासा',
    'अख्तियारी प्राप्त',
    'खर्च',
    'बाँकि मौज्दात',
    'कुल बजेट माग',
    'कुल बजेट निकासा',
    'कुल अख्तियारी प्राप्त',
    'कुल खर्च',
    'जम्मा मौज्दात',
    'भिन्नता'
  ];
  excelData.push(subHeader);
  
  // Capital Expenditure Section
  excelData.push(['पुँजीगत खर्च', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  
  // Capital items data
  capitalItems.forEach(item => {
    const row = [
      item.code_number || item.codeNumber || '',
      item.expense_title || item.expenseTitle || '',
      parseNumericValue(item.unallocated_budget || item.unallocatedBudget),
      parseNumericValue(item.revised_budget || item.revisedBudget),
      parseNumericValue(item.q1_budget_demand || item.q1BudgetDemand),
      parseNumericValue(item.q1_budget_expenditure || item.q1BudgetExpenditure),
      parseNumericValue(item.q1_authority_received || item.q1AuthorityReceived),
      parseNumericValue(item.q1_expense || item.q1Expense),
      parseNumericValue(item.q1_remaining_balance || item.q1RemainingBalance),
      parseNumericValue(item.q2_budget_demand || item.q2BudgetDemand),
      parseNumericValue(item.q2_budget_expenditure || item.q2BudgetExpenditure),
      parseNumericValue(item.q2_authority_received || item.q2AuthorityReceived),
      parseNumericValue(item.q2_expense || item.q2Expense),
      parseNumericValue(item.q2_remaining_balance || item.q2RemainingBalance),
      parseNumericValue(item.q3_budget_demand || item.q3BudgetDemand),
      parseNumericValue(item.q3_budget_expenditure || item.q3BudgetExpenditure),
      parseNumericValue(item.q3_authority_received || item.q3AuthorityReceived),
      parseNumericValue(item.q3_expense || item.q3Expense),
      parseNumericValue(item.q3_remaining_balance || item.q3RemainingBalance),
      parseNumericValue(item.q4_budget_demand || item.q4BudgetDemand),
      parseNumericValue(item.q4_budget_expenditure || item.q4BudgetExpenditure),
      parseNumericValue(item.q4_authority_received || item.q4AuthorityReceived),
      parseNumericValue(item.q4_expense || item.q4Expense),
      parseNumericValue(item.q4_remaining_balance || item.q4RemainingBalance),
      parseNumericValue(item.total_budget_demand || item.totalBudgetDemand),
      parseNumericValue(item.total_budget_expenditure || item.totalBudgetExpenditure),
      parseNumericValue(item.total_authority_received || item.totalAuthorityReceived),
      parseNumericValue(item.total_expense || item.totalExpense),
      parseNumericValue(item.total_remaining_balance || item.totalRemainingBalance),
      parseNumericValue(item.budget_allocation_difference || item.budgetAllocationDifference)
    ];
    excelData.push(row);
  });
  
  // Capital total row
  const capitalTotals = calculateTotals(capitalItems);
  excelData.push([
    'जम्मा पुँजीगत',
    '',
    capitalTotals.unallocatedBudget,
    capitalTotals.revisedBudget,
    capitalTotals.q1BudgetDemand,
    capitalTotals.q1BudgetExpenditure,
    capitalTotals.q1AuthorityReceived,
    capitalTotals.q1Expense,
    capitalTotals.q1RemainingBalance,
    capitalTotals.q2BudgetDemand,
    capitalTotals.q2BudgetExpenditure,
    capitalTotals.q2AuthorityReceived,
    capitalTotals.q2Expense,
    capitalTotals.q2RemainingBalance,
    capitalTotals.q3BudgetDemand,
    capitalTotals.q3BudgetExpenditure,
    capitalTotals.q3AuthorityReceived,
    capitalTotals.q3Expense,
    capitalTotals.q3RemainingBalance,
    capitalTotals.q4BudgetDemand,
    capitalTotals.q4BudgetExpenditure,
    capitalTotals.q4AuthorityReceived,
    capitalTotals.q4Expense,
    capitalTotals.q4RemainingBalance,
    capitalTotals.totalBudgetDemand,
    capitalTotals.totalBudgetExpenditure,
    capitalTotals.totalAuthorityReceived,
    capitalTotals.totalExpense,
    capitalTotals.totalRemainingBalance,
    capitalTotals.budgetAllocationDifference
  ]);
  
  // Empty row
  excelData.push([]);
  
  // Operational Expenditure Section
  excelData.push(['संचालन खर्च', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
  
  // Operational items data
  operationalItems.forEach(item => {
    const row = [
      item.code_number || item.codeNumber || '',
      item.expense_title || item.expenseTitle || '',
      parseNumericValue(item.unallocated_budget || item.unallocatedBudget),
      parseNumericValue(item.revised_budget || item.revisedBudget),
      parseNumericValue(item.q1_budget_demand || item.q1BudgetDemand),
      parseNumericValue(item.q1_budget_expenditure || item.q1BudgetExpenditure),
      parseNumericValue(item.q1_authority_received || item.q1AuthorityReceived),
      parseNumericValue(item.q1_expense || item.q1Expense),
      parseNumericValue(item.q1_remaining_balance || item.q1RemainingBalance),
      parseNumericValue(item.q2_budget_demand || item.q2BudgetDemand),
      parseNumericValue(item.q2_budget_expenditure || item.q2BudgetExpenditure),
      parseNumericValue(item.q2_authority_received || item.q2AuthorityReceived),
      parseNumericValue(item.q2_expense || item.q2Expense),
      parseNumericValue(item.q2_remaining_balance || item.q2RemainingBalance),
      parseNumericValue(item.q3_budget_demand || item.q3BudgetDemand),
      parseNumericValue(item.q3_budget_expenditure || item.q3BudgetExpenditure),
      parseNumericValue(item.q3_authority_received || item.q3AuthorityReceived),
      parseNumericValue(item.q3_expense || item.q3Expense),
      parseNumericValue(item.q3_remaining_balance || item.q3RemainingBalance),
      parseNumericValue(item.q4_budget_demand || item.q4BudgetDemand),
      parseNumericValue(item.q4_budget_expenditure || item.q4BudgetExpenditure),
      parseNumericValue(item.q4_authority_received || item.q4AuthorityReceived),
      parseNumericValue(item.q4_expense || item.q4Expense),
      parseNumericValue(item.q4_remaining_balance || item.q4RemainingBalance),
      parseNumericValue(item.total_budget_demand || item.totalBudgetDemand),
      parseNumericValue(item.total_budget_expenditure || item.totalBudgetExpenditure),
      parseNumericValue(item.total_authority_received || item.totalAuthorityReceived),
      parseNumericValue(item.total_expense || item.totalExpense),
      parseNumericValue(item.total_remaining_balance || item.totalRemainingBalance),
      parseNumericValue(item.budget_allocation_difference || item.budgetAllocationDifference)
    ];
    excelData.push(row);
  });
  
  // Operational total row
  const operationalTotals = calculateTotals(operationalItems);
  excelData.push([
    'जम्मा संचालन',
    '',
    operationalTotals.unallocatedBudget,
    operationalTotals.revisedBudget,
    operationalTotals.q1BudgetDemand,
    operationalTotals.q1BudgetExpenditure,
    operationalTotals.q1AuthorityReceived,
    operationalTotals.q1Expense,
    operationalTotals.q1RemainingBalance,
    operationalTotals.q2BudgetDemand,
    operationalTotals.q2BudgetExpenditure,
    operationalTotals.q2AuthorityReceived,
    operationalTotals.q2Expense,
    operationalTotals.q2RemainingBalance,
    operationalTotals.q3BudgetDemand,
    operationalTotals.q3BudgetExpenditure,
    operationalTotals.q3AuthorityReceived,
    operationalTotals.q3Expense,
    operationalTotals.q3RemainingBalance,
    operationalTotals.q4BudgetDemand,
    operationalTotals.q4BudgetExpenditure,
    operationalTotals.q4AuthorityReceived,
    operationalTotals.q4Expense,
    operationalTotals.q4RemainingBalance,
    operationalTotals.totalBudgetDemand,
    operationalTotals.totalBudgetExpenditure,
    operationalTotals.totalAuthorityReceived,
    operationalTotals.totalExpense,
    operationalTotals.totalRemainingBalance,
    operationalTotals.budgetAllocationDifference
  ]);
  
  // Grand total row
  const grandTotals = {
    unallocatedBudget: capitalTotals.unallocatedBudget + operationalTotals.unallocatedBudget,
    revisedBudget: capitalTotals.revisedBudget + operationalTotals.revisedBudget,
    q1BudgetDemand: capitalTotals.q1BudgetDemand + operationalTotals.q1BudgetDemand,
    q1BudgetExpenditure: capitalTotals.q1BudgetExpenditure + operationalTotals.q1BudgetExpenditure,
    q1AuthorityReceived: capitalTotals.q1AuthorityReceived + operationalTotals.q1AuthorityReceived,
    q1Expense: capitalTotals.q1Expense + operationalTotals.q1Expense,
    q1RemainingBalance: capitalTotals.q1RemainingBalance + operationalTotals.q1RemainingBalance,
    q2BudgetDemand: capitalTotals.q2BudgetDemand + operationalTotals.q2BudgetDemand,
    q2BudgetExpenditure: capitalTotals.q2BudgetExpenditure + operationalTotals.q2BudgetExpenditure,
    q2AuthorityReceived: capitalTotals.q2AuthorityReceived + operationalTotals.q2AuthorityReceived,
    q2Expense: capitalTotals.q2Expense + operationalTotals.q2Expense,
    q2RemainingBalance: capitalTotals.q2RemainingBalance + operationalTotals.q2RemainingBalance,
    q3BudgetDemand: capitalTotals.q3BudgetDemand + operationalTotals.q3BudgetDemand,
    q3BudgetExpenditure: capitalTotals.q3BudgetExpenditure + operationalTotals.q3BudgetExpenditure,
    q3AuthorityReceived: capitalTotals.q3AuthorityReceived + operationalTotals.q3AuthorityReceived,
    q3Expense: capitalTotals.q3Expense + operationalTotals.q3Expense,
    q3RemainingBalance: capitalTotals.q3RemainingBalance + operationalTotals.q3RemainingBalance,
    q4BudgetDemand: capitalTotals.q4BudgetDemand + operationalTotals.q4BudgetDemand,
    q4BudgetExpenditure: capitalTotals.q4BudgetExpenditure + operationalTotals.q4BudgetExpenditure,
    q4AuthorityReceived: capitalTotals.q4AuthorityReceived + operationalTotals.q4AuthorityReceived,
    q4Expense: capitalTotals.q4Expense + operationalTotals.q4Expense,
    q4RemainingBalance: capitalTotals.q4RemainingBalance + operationalTotals.q4RemainingBalance,
    totalBudgetDemand: capitalTotals.totalBudgetDemand + operationalTotals.totalBudgetDemand,
    totalBudgetExpenditure: capitalTotals.totalBudgetExpenditure + operationalTotals.totalBudgetExpenditure,
    totalAuthorityReceived: capitalTotals.totalAuthorityReceived + operationalTotals.totalAuthorityReceived,
    totalExpense: capitalTotals.totalExpense + operationalTotals.totalExpense,
    totalRemainingBalance: capitalTotals.totalRemainingBalance + operationalTotals.totalRemainingBalance,
    budgetAllocationDifference: capitalTotals.budgetAllocationDifference + operationalTotals.budgetAllocationDifference
  };
  
  excelData.push([
    'जम्मा कुल',
    '',
    grandTotals.unallocatedBudget,
    grandTotals.revisedBudget,
    grandTotals.q1BudgetDemand,
    grandTotals.q1BudgetExpenditure,
    grandTotals.q1AuthorityReceived,
    grandTotals.q1Expense,
    grandTotals.q1RemainingBalance,
    grandTotals.q2BudgetDemand,
    grandTotals.q2BudgetExpenditure,
    grandTotals.q2AuthorityReceived,
    grandTotals.q2Expense,
    grandTotals.q2RemainingBalance,
    grandTotals.q3BudgetDemand,
    grandTotals.q3BudgetExpenditure,
    grandTotals.q3AuthorityReceived,
    grandTotals.q3Expense,
    grandTotals.q3RemainingBalance,
    grandTotals.q4BudgetDemand,
    grandTotals.q4BudgetExpenditure,
    grandTotals.q4AuthorityReceived,
    grandTotals.q4Expense,
    grandTotals.q4RemainingBalance,
    grandTotals.totalBudgetDemand,
    grandTotals.totalBudgetExpenditure,
    grandTotals.totalAuthorityReceived,
    grandTotals.totalExpense,
    grandTotals.totalRemainingBalance,
    grandTotals.budgetAllocationDifference
  ]);
  
  return excelData;
};

/**
 * Generate organized Excel data with separate sections for each expenditure record
 */
export const generateOrganizedExcelData = (expenditureRecords) => {
  const excelData = [];
  
  // Process each expenditure record separately
  expenditureRecords.forEach((expenditure, index) => {
    // Add section separator (except for first record)
    if (index > 0) {
      // Add multiple empty rows for clear separation
      for (let i = 0; i < 3; i++) {
        excelData.push([]);
      }
      
      // Add section divider
      excelData.push(['='.repeat(100)]);
      excelData.push([]);
    }
    
    // Add expenditure record header
    excelData.push([`📋 रेकर्ड ${index + 1}: ${expenditure.institution_name || 'Unknown Institution'}`]);
    excelData.push([`📅 आर्थिक वर्ष: ${expenditure.fiscal_year_display || 'N/A'}`]);
    excelData.push([`📊 स्थिति: ${expenditure.status || 'N/A'}`]);
    excelData.push([`🆔 टोकन: ${expenditure.token || 'N/A'}`]);
    excelData.push([]); // Empty row
    
    // Add the standard table structure for this record
    const recordData = generateExcelData({
      institution: { name: expenditure.institution_name },
      fiscal_year: { display: expenditure.fiscal_year_display },
      expenditure_items: expenditure.expenditure_items || []
    });
    
    // Add the record data (skip the first 4 rows as we already added custom headers)
    excelData.push(...recordData.slice(4));
  });
  
  return excelData;
};

/**
 * Calculate totals for a set of items
 */
const calculateTotals = (items) => {
  return items.reduce((totals, item) => ({
    unallocatedBudget: totals.unallocatedBudget + parseNumericValue(item.unallocated_budget || item.unallocatedBudget),
    revisedBudget: totals.revisedBudget + parseNumericValue(item.revised_budget || item.revisedBudget),
    q1BudgetDemand: totals.q1BudgetDemand + parseNumericValue(item.q1_budget_demand || item.q1BudgetDemand),
    q1BudgetExpenditure: totals.q1BudgetExpenditure + parseNumericValue(item.q1_budget_expenditure || item.q1BudgetExpenditure),
    q1AuthorityReceived: totals.q1AuthorityReceived + parseNumericValue(item.q1_authority_received || item.q1AuthorityReceived),
    q1Expense: totals.q1Expense + parseNumericValue(item.q1_expense || item.q1Expense),
    q1RemainingBalance: totals.q1RemainingBalance + parseNumericValue(item.q1_remaining_balance || item.q1RemainingBalance),
    q2BudgetDemand: totals.q2BudgetDemand + parseNumericValue(item.q2_budget_demand || item.q2BudgetDemand),
    q2BudgetExpenditure: totals.q2BudgetExpenditure + parseNumericValue(item.q2_budget_expenditure || item.q2BudgetExpenditure),
    q2AuthorityReceived: totals.q2AuthorityReceived + parseNumericValue(item.q2_authority_received || item.q2AuthorityReceived),
    q2Expense: totals.q2Expense + parseNumericValue(item.q2_expense || item.q2Expense),
    q2RemainingBalance: totals.q2RemainingBalance + parseNumericValue(item.q2_remaining_balance || item.q2RemainingBalance),
    q3BudgetDemand: totals.q3BudgetDemand + parseNumericValue(item.q3_budget_demand || item.q3BudgetDemand),
    q3BudgetExpenditure: totals.q3BudgetExpenditure + parseNumericValue(item.q3_budget_expenditure || item.q3BudgetExpenditure),
    q3AuthorityReceived: totals.q3AuthorityReceived + parseNumericValue(item.q3_authority_received || item.q3AuthorityReceived),
    q3Expense: totals.q3Expense + parseNumericValue(item.q3_expense || item.q3Expense),
    q3RemainingBalance: totals.q3RemainingBalance + parseNumericValue(item.q3_remaining_balance || item.q3RemainingBalance),
    q4BudgetDemand: totals.q4BudgetDemand + parseNumericValue(item.q4_budget_demand || item.q4BudgetDemand),
    q4BudgetExpenditure: totals.q4BudgetExpenditure + parseNumericValue(item.q4_budget_expenditure || item.q4BudgetExpenditure),
    q4AuthorityReceived: totals.q4AuthorityReceived + parseNumericValue(item.q4_authority_received || item.q4AuthorityReceived),
    q4Expense: totals.q4Expense + parseNumericValue(item.q4_expense || item.q4Expense),
    q4RemainingBalance: totals.q4RemainingBalance + parseNumericValue(item.q4_remaining_balance || item.q4RemainingBalance),
    totalBudgetDemand: totals.totalBudgetDemand + parseNumericValue(item.total_budget_demand || item.totalBudgetDemand),
    totalBudgetExpenditure: totals.totalBudgetExpenditure + parseNumericValue(item.total_budget_expenditure || item.totalBudgetExpenditure),
    totalAuthorityReceived: totals.totalAuthorityReceived + parseNumericValue(item.total_authority_received || item.totalAuthorityReceived),
    totalExpense: totals.totalExpense + parseNumericValue(item.total_expense || item.totalExpense),
    totalRemainingBalance: totals.totalRemainingBalance + parseNumericValue(item.total_remaining_balance || item.totalRemainingBalance),
    budgetAllocationDifference: totals.budgetAllocationDifference + parseNumericValue(item.budget_allocation_difference || item.budgetAllocationDifference)
  }), {
    unallocatedBudget: 0, revisedBudget: 0,
    q1BudgetDemand: 0, q1BudgetExpenditure: 0, q1AuthorityReceived: 0, q1Expense: 0, q1RemainingBalance: 0,
    q2BudgetDemand: 0, q2BudgetExpenditure: 0, q2AuthorityReceived: 0, q2Expense: 0, q2RemainingBalance: 0,
    q3BudgetDemand: 0, q3BudgetExpenditure: 0, q3AuthorityReceived: 0, q3Expense: 0, q3RemainingBalance: 0,
    q4BudgetDemand: 0, q4BudgetExpenditure: 0, q4AuthorityReceived: 0, q4Expense: 0, q4RemainingBalance: 0,
    totalBudgetDemand: 0, totalBudgetExpenditure: 0, totalAuthorityReceived: 0, totalExpense: 0, totalRemainingBalance: 0,
    budgetAllocationDifference: 0
  });
};

/**
 * Export data to Excel with proper formatting
 */
export const exportToExcel = (expenditureData, filename = 'capital_expenditure') => {
  try {
    // Filter items for styling calculations
    const capitalItems = expenditureData.expenditure_items?.filter(item => item.item_type === 'capital' || item.type === 'capital') || [];
    const operationalItems = expenditureData.expenditure_items?.filter(item => item.item_type === 'operational' || item.type === 'operational') || [];
    
    // Generate Excel data
    const excelData = generateExcelData(expenditureData);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths for 30 columns - optimized for better readability
    const columnWidths = [
      { wch: 15 }, // कोड नं.
      { wch: 35 }, // खर्च शिर्षक
      { wch: 18 }, // बिनियोजित बजेट
      { wch: 18 }, // परिमार्जीत बजेट
      { wch: 14 }, // Q1 बजेट माग
      { wch: 14 }, // Q1 बजेट निकासा
      { wch: 14 }, // Q1 अख्तियारी प्राप्त
      { wch: 14 }, // Q1 खर्च
      { wch: 14 }, // Q1 बाँकि मौज्दात
      { wch: 14 }, // Q2 बजेट माग
      { wch: 14 }, // Q2 बजेट निकासा
      { wch: 14 }, // Q2 अख्तियारी प्राप्त
      { wch: 14 }, // Q2 खर्च
      { wch: 14 }, // Q2 बाँकि मौज्दात
      { wch: 14 }, // Q3 बजेट माग
      { wch: 14 }, // Q3 बजेट निकासा
      { wch: 14 }, // Q3 अख्तियारी प्राप्त
      { wch: 14 }, // Q3 खर्च
      { wch: 14 }, // Q3 बाँकि मौज्दात
      { wch: 14 }, // Q4 बजेट माग
      { wch: 14 }, // Q4 बजेट निकासा
      { wch: 14 }, // Q4 अख्तियारी प्राप्त
      { wch: 14 }, // Q4 खर्च
      { wch: 14 }, // Q4 बाँकि मौज्दात
      { wch: 18 }, // कुल बजेट माग
      { wch: 18 }, // कुल बजेट निकासा
      { wch: 18 }, // कुल अख्तियारी प्राप्त
      { wch: 18 }, // कुल खर्च
      { wch: 18 }, // जम्मा मौज्दात
      { wch: 18 }  // भिन्नता
    ];
    worksheet['!cols'] = columnWidths;
    
    // Add styles to cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;
        
        // Apply styles based on row and content
        if (R === 0) {
          // University title - large, bold, dark blue
          worksheet[cellAddress].s = {
            font: { bold: true, size: 18, color: { rgb: "1E3C72" } },
            alignment: { horizontal: "center", vertical: "center" }
          };
        } else if (R === 1) {
          // Institution name - medium, bold, blue
          worksheet[cellAddress].s = {
            font: { bold: true, size: 16, color: { rgb: "2E5A92" } },
            alignment: { horizontal: "center", vertical: "center" }
          };
        } else if (R === 2) {
          // Fiscal year - medium, bold, dark gray
          worksheet[cellAddress].s = {
            font: { bold: true, size: 14, color: { rgb: "495057" } },
            alignment: { horizontal: "center", vertical: "center" }
          };
        } else if (R === 4) {
          // Main header - dark yellow/orange with white text
          worksheet[cellAddress].s = EXCEL_STYLES.header;
        } else if (R === 5) {
          // Sub header - dark yellow/orange with white text
          worksheet[cellAddress].s = EXCEL_STYLES.subHeader;
        } else if (R === 6 || R === 6 + capitalItems.length + 2) {
          // Category headers - light green with black text
          worksheet[cellAddress].s = EXCEL_STYLES.categoryHeader;
        } else if (R === 6 + capitalItems.length + 1 || 
                   R === 6 + capitalItems.length + 2 + operationalItems.length + 1 ||
                   R === 6 + capitalItems.length + 2 + operationalItems.length + 2) {
          // Total rows - light green with black bold text
          worksheet[cellAddress].s = EXCEL_STYLES.totalRow;
          
          // Right-align numbers in total rows
          if (C >= 2 && typeof worksheet[cellAddress].v === 'number') {
            worksheet[cellAddress].s.alignment = { horizontal: "right", vertical: "center" };
          }
        } else {
          // Data cells - white background with black borders
          worksheet[cellAddress].s = EXCEL_STYLES.dataCell;
          
          // Left-align text in first two columns (code and title)
          if (C === 0 || C === 1) {
            worksheet[cellAddress].s.alignment = { horizontal: "left", vertical: "center" };
          }
          
          // Right-align numbers in other columns
          if (C >= 2 && typeof worksheet[cellAddress].v === 'number') {
            worksheet[cellAddress].s.alignment = { horizontal: "right", vertical: "center" };
            worksheet[cellAddress].s = { ...worksheet[cellAddress].s, ...EXCEL_STYLES.numberFormat };
          }
        }
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Capital Expenditure');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    // Write and download file
    XLSX.writeFile(workbook, finalFilename);
    
    return { success: true, filename: finalFilename };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export organized data to Excel with separate sections for each record
 */
export const exportOrganizedToExcel = (expenditureRecords, filename = 'organized_expenditure_data') => {
  try {
    // Generate organized Excel data
    const excelData = generateOrganizedExcelData(expenditureRecords);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths for 30 columns - optimized for better readability
    const columnWidths = [
      { wch: 15 }, // कोड नं.
      { wch: 35 }, // खर्च शिर्षक
      { wch: 18 }, // बिनियोजित बजेट
      { wch: 18 }, // परिमार्जीत बजेट
      { wch: 14 }, // Q1 बजेट माग
      { wch: 14 }, // Q1 बजेट निकासा
      { wch: 14 }, // Q1 अख्तियारी प्राप्त
      { wch: 14 }, // Q1 खर्च
      { wch: 14 }, // Q1 बाँकि मौज्दात
      { wch: 14 }, // Q2 बजेट माग
      { wch: 14 }, // Q2 बजेट निकासा
      { wch: 14 }, // Q2 अख्तियारी प्राप्त
      { wch: 14 }, // Q2 खर्च
      { wch: 14 }, // Q2 बाँकि मौज्दात
      { wch: 14 }, // Q3 बजेट माग
      { wch: 14 }, // Q3 बजेट निकासा
      { wch: 14 }, // Q3 अख्तियारी प्राप्त
      { wch: 14 }, // Q3 खर्च
      { wch: 14 }, // Q3 बाँकि मौज्दात
      { wch: 14 }, // Q4 बजेट माग
      { wch: 14 }, // Q4 बजेट निकासा
      { wch: 14 }, // Q4 अख्तियारी प्राप्त
      { wch: 14 }, // Q4 खर्च
      { wch: 14 }, // Q4 बाँकि मौज्दात
      { wch: 18 }, // कुल बजेट माग
      { wch: 18 }, // कुल बजेट निकासा
      { wch: 18 }, // कुल अख्तियारी प्राप्त
      { wch: 18 }, // कुल खर्च
      { wch: 18 }, // जम्मा मौज्दात
      { wch: 18 }  // भिन्नता
    ];
    worksheet['!cols'] = columnWidths;
    
    // Add styles to cells
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;
        
        // Apply styles based on row content and position
        const cellValue = worksheet[cellAddress].v;
        
        if (typeof cellValue === 'string') {
          // Record headers (with emojis)
          if (cellValue.includes('📋 रेकर्ड') || cellValue.includes('📅 आर्थिक वर्ष') || 
              cellValue.includes('📊 स्थिति') || cellValue.includes('🆔 टोकन')) {
            worksheet[cellAddress].s = {
              font: { bold: true, size: 14, color: { rgb: "1E3C72" } },
              fill: { fgColor: { rgb: "E3F2FD" } },
              alignment: { horizontal: "left", vertical: "center" }
            };
          }
          // Section dividers
          else if (cellValue.startsWith('=')) {
            worksheet[cellAddress].s = {
              font: { bold: true, color: { rgb: "666666" } },
              fill: { fgColor: { rgb: "F5F5F5" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // University title
          else if (cellValue === 'मध्यपश्चिम विश्वबिधालय') {
            worksheet[cellAddress].s = {
              font: { bold: true, size: 18, color: { rgb: "1E3C72" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Institution name
          else if (cellValue && !cellValue.includes('आ .व') && !cellValue.includes('बजेट माग तथा खर्च विवरण')) {
            // Check if this is likely an institution name (not empty, not fiscal year)
            const isInstitutionName = cellValue.length > 0 && 
              !cellValue.includes('प्रथम') && !cellValue.includes('दोस्रो') && 
              !cellValue.includes('तेस्रो') && !cellValue.includes('चौथो') &&
              !cellValue.includes('यस आ.व.को') && !cellValue.includes('विनियोजित');
            
            if (isInstitutionName && R > 0) {
              worksheet[cellAddress].s = {
                font: { bold: true, size: 16, color: { rgb: "2E5A92" } },
                alignment: { horizontal: "center", vertical: "center" }
              };
            }
          }
          // Fiscal year title
          else if (cellValue.includes('आ .व') && cellValue.includes('बजेट माग तथा खर्च विवरण')) {
            worksheet[cellAddress].s = {
              font: { bold: true, size: 14, color: { rgb: "495057" } },
              alignment: { horizontal: "center", vertical: "center" }
            };
          }
          // Category headers (Capital/Operational)
          else if (cellValue === 'पुँजीगत खर्च' || cellValue === 'संचालन खर्च') {
            worksheet[cellAddress].s = EXCEL_STYLES.categoryHeader;
          }
          // Total rows
          else if (cellValue.startsWith('जम्मा')) {
            worksheet[cellAddress].s = EXCEL_STYLES.totalRow;
          }
          // Main headers (with column spans)
          else if (['कोड नं.', 'खर्च शिर्षक', 'बिनियोजित बजेट', 'परिमार्जीत बजेट'].includes(cellValue)) {
            worksheet[cellAddress].s = EXCEL_STYLES.header;
          }
          // Sub headers
          else if (['बजेट माग', 'बजेट निकासा', 'अख्तियारी प्राप्त', 'खर्च', 'बाँकि मौज्दात'].includes(cellValue)) {
            worksheet[cellAddress].s = EXCEL_STYLES.subHeader;
          }
          // Quarter headers
          else if (['प्रथम त्रैमासिक', 'दोस्रो त्रैमासिक', 'तेस्रो त्रैमासिक', 'चौथो त्रैमासिक'].includes(cellValue)) {
            worksheet[cellAddress].s = EXCEL_STYLES.header;
          }
          // Total section headers
          else if (['यस आ.व.को कुल बजेट विवरण', 'विनियोजित बजेट र अर्च विचको भिन्नता'].includes(cellValue)) {
            worksheet[cellAddress].s = EXCEL_STYLES.header;
          }
          // Data cells (code numbers and titles)
          else if (cellValue && cellValue.length > 0) {
            worksheet[cellAddress].s = EXCEL_STYLES.dataCell;
            
            // Left-align text in first two columns (code and title)
            if (C === 0 || C === 1) {
              worksheet[cellAddress].s.alignment = { horizontal: "left", vertical: "center" };
            }
          }
        }
        // Number cells
        else if (typeof cellValue === 'number') {
          worksheet[cellAddress].s = {
            ...EXCEL_STYLES.dataCell,
            ...EXCEL_STYLES.numberFormat,
            alignment: { horizontal: "right", vertical: "center" }
          };
        }
        // Empty cells - apply borders
        else if (cellValue === '' || cellValue === null || cellValue === undefined) {
          worksheet[cellAddress].s = {
            fill: { fgColor: { rgb: "FFFFFF" } },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } }
            }
          };
        }
      }
    }
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Organized Expenditure Data');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    // Write and download file
    XLSX.writeFile(workbook, finalFilename);
    
    return { success: true, filename: finalFilename };
  } catch (error) {
    console.error('Error exporting organized Excel:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Export current form data to Excel (for new data entry)
 */
export const exportFormDataToExcel = (formData, institution, fiscalYear) => {
  const mockExpenditureData = {
    institution: institution,
    fiscal_year: fiscalYear,
    expenditure_items: [
      ...formData.rows.map(row => ({
        ...row,
        item_type: 'capital'
      })),
      ...formData.operationalRows.map(row => ({
        ...row,
        item_type: 'operational'
      }))
    ]
  };
  
  // Use organized export for consistency with other exports
  const singleRecord = [{
    institution_name: institution?.name || 'केन्द्रीय कार्यालय',
    fiscal_year_display: fiscalYear?.display || '',
    status: 'मस्यौदा',
    token: 'नयाँ डाटा',
    expenditure_items: mockExpenditureData.expenditure_items
  }];
  
  return exportOrganizedToExcel(singleRecord, 'new_expenditure_data');
};

// Function to export summary table data to Excel
export const exportSummaryTableToExcel = (tableData, fileName = 'खर्च_सारांश_तालिका') => {
  try {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    
    // Prepare data for Excel
    const excelData = [];
    
    // Add headers
    const headers = ['बजेट कोड', 'बजेट शिर्षक'];
    
    // Get unique institutions from the data
    const institutions = [...new Set(tableData.map(item => item.institution_name).filter(Boolean))];
    institutions.forEach(institution => {
      headers.push(institution);
    });
    headers.push('जम्मा');
    
    excelData.push(headers);
    
    // Add data rows
    tableData.forEach(item => {
      const row = [
        item.code_number || item.budgetCode || '',
        item.expense_title || item.expenditureTitle || item.title || ''
      ];
      
      // Add values for each institution
      institutions.forEach(institution => {
        const institutionData = item.institutions?.[institution];
        if (institutionData) {
          const totalBudget = Object.values(institutionData).reduce((sum, value) => sum + parseFloat(value || 0), 0);
          row.push(totalBudget);
        } else {
          row.push(0);
        }
      });
      
      // Add total
      const grandTotal = institutions.reduce((sum, institution) => {
        const institutionData = item.institutions?.[institution];
        if (institutionData) {
          return sum + Object.values(institutionData).reduce((instSum, value) => instSum + parseFloat(value || 0), 0);
        }
        return sum;
      }, 0);
      row.push(grandTotal);
      
      excelData.push(row);
    });
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 15 }, // बजेट कोड
      { wch: 40 }, // बजेट शिर्षक
    ];
    
    // Add widths for institution columns
    institutions.forEach(() => {
      columnWidths.push({ wch: 20 });
    });
    
    columnWidths.push({ wch: 20 }); // जम्मा column
    
    worksheet['!cols'] = columnWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'खर्च सारांश');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fullFileName = `${fileName}_${timestamp}.xlsx`;
    
    // Write to file
    XLSX.writeFile(workbook, fullFileName);
    
    return { success: true, fileName: fullFileName };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, error: error.message };
  }
};

// Function to export detailed summary table with capital and operational sections
export const exportDetailedSummaryTableToExcel = (allExpenditureItems, institutions, fileName = 'विस्तृत_खर्च_सारांश') => {
  try {
    const workbook = XLSX.utils.book_new();
    
    // Process data for Excel
    const excelData = [];
    
    // Add headers
    const headers = ['बजेट कोड', 'बजेट शिर्षक'];
    institutions.forEach(institution => {
      headers.push(institution);
    });
    headers.push('जम्मा');
    
    excelData.push(headers);
    
    // Add Capital Expenditure Section Header
    excelData.push(['', 'पुँजीगत खर्च जम्मा', ...institutions.map(() => ''), '']);
    
    // Calculate capital expenditure totals
    const capitalTotals = institutions.map(institution => {
      return allExpenditureItems
        .filter(item => (item.item_type === 'capital' || 
                        (item.code_number && item.code_number.startsWith('01.'))) &&
                        item.institution_name === institution)
        .reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
    });
    
    const capitalGrandTotal = capitalTotals.reduce((sum, total) => sum + total, 0);
    excelData.push(['', 'पुँजीगत खर्च जम्मा', ...capitalTotals, capitalGrandTotal]);
    
    // Add individual capital items
    const capitalItems = allExpenditureItems.filter(item => 
      item.item_type === 'capital' || 
      (item.code_number && item.code_number.startsWith('01.'))
    );
    
    const uniqueCapitalCodes = [...new Set(capitalItems.map(item => item.code_number))];
    
    uniqueCapitalCodes.forEach(code => {
      const itemsWithCode = capitalItems.filter(item => item.code_number === code);
      const title = itemsWithCode[0]?.expense_title || itemsWithCode[0]?.title || '';
      
      const row = [code, title];
      
      institutions.forEach(institution => {
        const institutionItems = itemsWithCode.filter(item => item.institution_name === institution);
        const total = institutionItems.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
        row.push(total);
      });
      
      const total = itemsWithCode.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
      row.push(total);
      
      excelData.push(row);
    });
    
    // Add Operational Expenditure Section Header
    excelData.push(['', '', ...institutions.map(() => ''), '']);
    excelData.push(['', 'संचालन खर्च जम्मा', ...institutions.map(() => ''), '']);
    
    // Calculate operational expenditure totals
    const operationalTotals = institutions.map(institution => {
      return allExpenditureItems
        .filter(item => (item.item_type === 'operational' || 
                        (item.code_number && item.code_number.startsWith('02.'))) &&
                        item.institution_name === institution)
        .reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
    });
    
    const operationalGrandTotal = operationalTotals.reduce((sum, total) => sum + total, 0);
    excelData.push(['', 'संचालन खर्च जम्मा', ...operationalTotals, operationalGrandTotal]);
    
    // Add individual operational items
    const operationalItems = allExpenditureItems.filter(item => 
      item.item_type === 'operational' || 
      (item.code_number && item.code_number.startsWith('02.'))
    );
    
    const uniqueOperationalCodes = [...new Set(operationalItems.map(item => item.code_number))];
    
    uniqueOperationalCodes.forEach(code => {
      const itemsWithCode = operationalItems.filter(item => item.code_number === code);
      const title = itemsWithCode[0]?.expense_title || itemsWithCode[0]?.title || '';
      
      const row = [code, title];
      
      institutions.forEach(institution => {
        const institutionItems = itemsWithCode.filter(item => item.institution_name === institution);
        const total = institutionItems.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
        row.push(total);
      });
      
      const total = itemsWithCode.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
      row.push(total);
      
      excelData.push(row);
    });
    
    // Add Grand Total
    excelData.push(['', '', ...institutions.map(() => ''), '']);
    excelData.push(['', 'जम्मा खर्च', ...institutions.map((_, index) => capitalTotals[index] + operationalTotals[index]), capitalGrandTotal + operationalGrandTotal]);
    
    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    
    // Define colors based on the image
    const colors = {
      headerBudget: { rgb: '8B0000' }, // Dark reddish-brown for budget code/title
      headerInstitution: { rgb: '2C3E50' }, // Dark blue/teal for institutions
      headerTotal: { rgb: '28A745' }, // Bright green for total column
      capitalTotal: { rgb: 'FFFACD' }, // Light yellow for capital total row
      operationalTotal: { rgb: '17A2B8' }, // Blue for operational total row
      totalColumn: { rgb: 'D4EDDA' }, // Light green for total column cells
      white: { rgb: 'FFFFFF' }, // White background
      black: { rgb: '000000' } // Black text
    };
    
    // Apply styling and colors
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    // Apply header styling
    for (let col = 0; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) {
        worksheet[cellAddress] = { v: '' };
      }
      
      if (col === 0 || col === 1) {
        // Budget code and title headers - dark reddish-brown
        worksheet[cellAddress].s = {
          fill: { fgColor: colors.headerBudget },
          font: { color: colors.white, bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: colors.black },
            bottom: { style: "thin", color: colors.black },
            left: { style: "thin", color: colors.black },
            right: { style: "thin", color: colors.black }
          }
        };
      } else if (col === range.e.c) {
        // Total column header - bright green
        worksheet[cellAddress].s = {
          fill: { fgColor: colors.headerTotal },
          font: { color: colors.white, bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: colors.black },
            bottom: { style: "thin", color: colors.black },
            left: { style: "thin", color: colors.black },
            right: { style: "thin", color: colors.black }
          }
        };
      } else {
        // Institution headers - dark blue/teal
        worksheet[cellAddress].s = {
          fill: { fgColor: colors.headerInstitution },
          font: { color: colors.white, bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: colors.black },
            bottom: { style: "thin", color: colors.black },
            left: { style: "thin", color: colors.black },
            right: { style: "thin", color: colors.black }
          }
        };
      }
    }
    
    // Apply row styling
    for (let row = 1; row <= range.e.r; row++) {
      const rowData = excelData[row];
      if (!rowData) continue;
      
      const isCapitalTotal = rowData[1] === 'पुँजीगत खर्च जम्मा';
      const isOperationalTotal = rowData[1] === 'संचालन खर्च जम्मा';
      const isGrandTotal = rowData[1] === 'जम्मा खर्च';
      
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { v: '' };
        }
        
        if (isCapitalTotal) {
          // Capital total row - light yellow background
          worksheet[cellAddress].s = {
            fill: { fgColor: colors.capitalTotal },
            font: { color: colors.black },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: colors.black },
              bottom: { style: "thin", color: colors.black },
              left: { style: "thin", color: colors.black },
              right: { style: "thin", color: colors.black }
            }
          };
        } else if (isOperationalTotal) {
          // Operational total row - blue background with white text
          worksheet[cellAddress].s = {
            fill: { fgColor: colors.operationalTotal },
            font: { color: colors.white, bold: true },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: colors.black },
              bottom: { style: "thin", color: colors.black },
              left: { style: "thin", color: colors.black },
              right: { style: "thin", color: colors.black }
            }
          };
        } else if (isGrandTotal) {
          // Grand total row - bold
          worksheet[cellAddress].s = {
            font: { bold: true, color: colors.black },
            fill: { fgColor: colors.white },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: colors.black },
              bottom: { style: "thin", color: colors.black },
              left: { style: "thin", color: colors.black },
              right: { style: "thin", color: colors.black }
            }
          };
        } else {
          // Regular data rows - white background
          worksheet[cellAddress].s = {
            fill: { fgColor: colors.white },
            font: { color: colors.black },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: colors.black },
              bottom: { style: "thin", color: colors.black },
              left: { style: "thin", color: colors.black },
              right: { style: "thin", color: colors.black }
            }
          };
        }
        
        // Total column styling - light green background for all rows
        if (col === range.e.c) {
          worksheet[cellAddress].s = {
            ...worksheet[cellAddress].s,
            fill: { fgColor: colors.totalColumn }
          };
        }
        
        // Number formatting for numerical values
        if (typeof worksheet[cellAddress].v === 'number') {
          worksheet[cellAddress].s = {
            ...worksheet[cellAddress].s,
            numFmt: "#,##0.00"
          };
        }
      }
    }
    
    // Set column widths
    const columnWidths = [
      { wch: 15 }, // बजेट कोड
      { wch: 40 }, // बजेट शिर्षक
    ];
    
    institutions.forEach(() => {
      columnWidths.push({ wch: 20 });
    });
    
    columnWidths.push({ wch: 20 }); // जम्मा column
    
    worksheet['!cols'] = columnWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'विस्तृत खर्च सारांश');
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fullFileName = `${fileName}_${timestamp}.xlsx`;
    
    // Write to file
    XLSX.writeFile(workbook, fullFileName);
    
    return { success: true, fileName: fullFileName };
  } catch (error) {
    console.error('Error exporting detailed summary to Excel:', error);
    return { success: false, error: error.message };
  }
};
